import { useStore } from '@tanstack/react-form';
import { CircleCheckBig, Clock } from 'lucide-react';

import Transition from '@/components/Common/Transition';
import { ScheduleSelect } from '@/components/ScheduleSelect/ScheduleSelect';
import TreeSelect from '@/components/Select/TreeSelect';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/shadcn/ui/select';
import { subscriptionFormOptions } from '@/entities/(notification)/subscription';
import dashboardTreeFetcher from '@/features/Dashboard/utils/dashboardTreeFetcher';
import { withForm } from '@/shared/ui/form';
import { getTimezoneList } from '@/utils/util';

const timezoneList = getTimezoneList().map((zone) => ({
  label: zone.name,
  value: zone.tzCode,
}));

export const SubscriptionTriggerSection = withForm({
  ...subscriptionFormOptions,
  render: function SubscriptionTriggerSection({ form }) {
    const target = useStore(form.store, (state) => state.values.target);

    return (
      <div className="flex flex-col gap-5 rounded-lg border p-5">
        {/* 헤더 */}
        <div className="flex items-center gap-2">
          <CircleCheckBig className="size-5 text-green-600" />
          <span className="text-sm font-semibold">Trigger</span>
        </div>

        {/* Dashboard 선택 */}
        <div className="grid grid-cols-[120px_1fr] items-center gap-3">
          <span className="text-sm text-muted-foreground">Report</span>
          <form.AppField
            name="target"
            children={(field) => (
              <TreeSelect
                mode="single"
                style={{ width: 300 }}
                placeholder="Select dashboard"
                values={field.state.value ? [field.state.value] : []}
                fetcher={dashboardTreeFetcher}
                onChange={(values) => {
                  field.handleChange(values[0] as string);
                }}
              />
            )}
          />
        </div>

        {/* Timezone (target 선택 후에만 표시) */}
        <Transition status={!!target}>
          <div className="grid grid-cols-[120px_1fr] items-center gap-3">
            <span className="text-sm text-muted-foreground">Local time zone</span>
            <form.AppField
              name="timezone"
              children={(field) => (
                <Select
                  value={field.state.value}
                  onValueChange={field.handleChange}
                >
                  <SelectTrigger className="w-[300px]">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    {timezoneList.map((tz) => (
                      <SelectItem key={tz.value} value={tz.value}>
                        {tz.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
          </div>
        </Transition>

        {/* Schedule (target 선택 후에만 표시) */}
        <Transition status={!!target}>
          <div className="grid grid-cols-[120px_1fr] items-center gap-3">
            <div className="flex items-center gap-1.5">
              <Clock className="size-4" />
              <span className="text-sm text-muted-foreground">When</span>
            </div>
            <form.AppField
              name="schedule"
              children={(field) => (
                <ScheduleSelect
                  schedule={field.state.value}
                  setSchedule={field.handleChange}
                />
              )}
            />
          </div>
        </Transition>
      </div>
    );
  },
});
